import { Switch } from '@headlessui/react'
import {
    DropDownList,
    DropDownListChangeEvent,
    ListItemProps,
} from '@progress/kendo-react-dropdowns'
import { Popup } from '@progress/kendo-react-popup'
import DatePickerInput from 'components/DateInput'
import ImageFileUploader from 'components/FileUploader/Image'
import FormInput from 'components/Forms/form-input'
import GenericError from 'components/GenericError/GenericError'
import Translation from 'components/Translation'
import {
    DEFAULT_ERROR_STATE,
    IError,
    useAppContext,
} from 'context/AppContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { cloneElement, useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { CognitoErrorTypes } from 'types/cognito'
import { EMAIL_REGEX, WORD_SINGLE_LINE_REGEX, PHONE_REGEX } from 'types/common'
import { Role, ROLES, StaffUser } from 'types/staff'
import { classNames } from 'utils/dom-helpers'
import { postApiRequest, putApiRequest, useFetch } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { FetchMethods } from 'utils/types'
import { SubmitError, UserModel } from '../types'
import { GeneralFormValues } from './types'
import { ImageIcon } from 'utils/custom-icons'

function GeneralForm({
    onCloseClick,
    onSubmitClick,
}: {
    onCloseClick(): void
    onSubmitClick(): void
}) {
    const { setGlobalError } = useAppErrorApiContext()
    const {
        setGlobalLoading,
        translations,
    } = useAppContext()
    const { StaffModal, user: userFromContext } = useAuth()
    const { getEntitlements, getAllStaff, tenant } = useTenantContext()
    const { attributes, setAttributes } = StaffModal
    const [staff_role, setStaffRole] = useState<Role>()
    const [date_joined, setDateJoined] = useState<Date>(
        new Date((attributes?.created_at as Date) || new Date())
    )
        console.log(attributes?.created_at,"attributes?.created_at");
        
    const [show_login_info, setShowLoginInfo] = useState(false)
    const login_anchor = useRef<HTMLButtonElement | null>(null)
    const [show_role_info, setShowRoleInfo] = useState(false)
    console.log(show_role_info)
    const role_anchor = useRef<HTMLButtonElement | null>(null)

    const [login, toggleLogin] = useState(
        (attributes?.login_enabled as boolean) || false
    )
    const [show_upload_view, setShowUploadView] = useState(true)
    const [staff_image, setStaffImage] = useState('')
    const [api_error, setApiError] = useState<IError>(DEFAULT_ERROR_STATE)
    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
    } = useForm<GeneralFormValues>({
        mode: 'onSubmit',
    })
    const { data: data_business } = useFetch(
        `/v1/tenants/get-by-user-id?userId=${userFromContext?.id}`,
        FetchMethods.GET,
        !!userFromContext?.id
    )

    const is_new = !attributes?.id
    const is_tenant = tenant?.user?.id == attributes?.user_id

    useEffect(() => {
        const staff_user = attributes?.user as StaffUser
        if (staff_user?.role && !staff_role) {
            setStaffRole(staff_user?.role)
        }
        if (attributes?.image_url && !staff_image) {
            setStaffImage(attributes?.image_url as string)
            setShowUploadView(false)
        }
    }, [attributes])

    const onSubmit: SubmitHandler<GeneralFormValues> = async (
        values: Record<string, string>
    ) => {
        setGlobalLoading(true)
        const { email, notes, phone, first_name, last_name } = values
        const staff_image = attributes?.image_url as string
        const defaultData =
            data_business[0]?.user?.id === attributes?.user_id
                ? { ...data_business[0].user }
                : {}
        const staff_user = attributes?.user as StaffUser
        const user: UserModel = {
            ...defaultData,
            cognitoId: staff_user?.cognito_id,
            firstName: first_name,
            lastName: last_name,
            email: email || staff_user?.email,
            imageUrl: staff_image || staff_user?.image_url,
            phone,
        }

        if (attributes?.id) {
            user.id = attributes.user_id as string
        }

        const staff = {
            cognitoId: attributes?.cognito_id as string,
            id: attributes?.id as string,
            tenant,
            hourlyWage: attributes?.hourly_rate as number,
            monthlyWage: attributes?.monthly_rate as number,
            overtimeRate: attributes?.overtime_rate as number,
            user,
            notes,
        }

        try {
            let result = null
            if (attributes?.id) {
                result = await putApiRequest(
                    `/v1/staff/${attributes?.id}`,
                    staff
                )
            } else {
                result = await postApiRequest(
                    `/v1/staff?withCognitoLogin=${login}`,
                    staff
                )
            }

            if (result.ok) {
                postApiRequest(
                    `/v1/user-roles/assign-role?tenantId=${tenant?.id}&userId=${result?.user?.id}&roleType=${staff_role?.id}&deleteExisting=true`
                )
                onSubmitClick()
            } else {
                throw {
                    message: result?.message as string,
                    tag: result?.tag as string,
                } as IError
            }
        } catch (err) {
            const { name, message, tag } = err as SubmitError & IError
            if (name == CognitoErrorTypes.UserExistsException) {
                setError('email', {
                    type: CognitoErrorTypes.UserExistsException,
                    message,
                })
            }
            setGlobalError(err)
        } finally {
            getEntitlements()
            getAllStaff()
            setGlobalLoading(false)
        }
    }

    async function handleLoginChange(val: boolean) {
        if (is_new) {
            toggleLogin(val)
        } else {
            try {
                setGlobalLoading(true)
                const action = val ? 'enable-cognito' : 'disable-cognito'
                const res = await putApiRequest(
                    `/v1/staff/${action}/${attributes?.id}`
                )
                if (!res.ok) {
                    throw {
                        message: `Error in updating staff login: ${res?.mesage}`,
                        tag: res?.tag,
                    } as IError
                }
                toggleLogin(val)
                const staff_user = attributes.user as StaffUser
                setAttributes({
                    ...attributes,
                    cognito_id: res.cognitoId,
                    user: {
                        ...staff_user,
                        cognito_id: res.user.cognitoId
                    }
                })
            } catch (err) {
                setGlobalError(err as IError)
            } finally {
                setGlobalLoading(false)
            }
        }
    }

    function handleRoleChange(e: DropDownListChangeEvent) {
        setStaffRole(e.target.value)
    }

    function RoleItemRender(
        li: React.ReactElement<HTMLLIElement>,
        item_props: ListItemProps
    ) {
        const { dataItem } = item_props
        const item_children = (
            <div className="m-2 p-2">
                <p className="font-semibold">
                    {translations[dataItem.text]}
                </p>
                <p>{translations[dataItem.desc]}</p>
            </div>
        )

        return cloneElement(li, li.props, item_children)
    }

    function RoleValueRender(
        element: React.ReactElement<HTMLSpanElement>,
        value: any
    ) {
        if (!value) {
            return element
        }

        const children = [
            <div key={1} className="px-4">
                {translations[value.text]}
            </div>,
        ]

        return cloneElement(element, { ...element.props }, children)
    }

    function handleOnAdd(image_url: string) {
        setStaffImage(image_url)
        setShowUploadView(false)
    }

    function UrlPreviewComponent(props: any) {
        return (
            <div className="profile-upload image-upload-location ">
                <div className="img-overlay">
                    <figure>
                        <img
                            src={props.image_url}
                            className=""
                            alt={props.image_alt}
                        />
                       
                    </figure>
                    <button
                            onClick={() => setShowUploadView(true)}
                            className="flex gap-3 items-center upload-btn  justify-center"
                        >
                          <i className="feather-edit " />

                        </button>
                    
                </div>
                <div className='profile-upload-details'>
                            <span className="text-lg">{props.image_alt}</span>
                            <span>Allowed file types: png, jpg, jpeg.</span>
                        </div>
                {/* <div className="">
                 <span className="text-lg">{props.image_alt}</span> 
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowUploadView(true)}
                            className="flex gap-3 items-center bg-primary-light text-white px-2 py-1 rounded-lg text-sm justify-center"
                        >
                            <ImageIcon color='white'/>
                            <span>{translations['replace_photo']}</span>
                        </button>
                    </div>
                </div> */}
            </div>
        )
    }

    function showUploadView() {
        if (!show_upload_view) {
            return (
                <UrlPreviewComponent
                    image_url={staff_image as string}
                    image_alt={translations['staff_profile_photo']}
                />
            )
        } else {
            return (
                <ImageFileUploader
                    tenant_id={tenant?.id}
                    onAdd={handleOnAdd}
                    staff_modal={true}
                    accept=".jpeg, .jpg, .gif, .png"
                    form_context={{
                        attributes: attributes || {},
                        setAttributes: setAttributes as unknown as (
                            data: Record<string, string | File>
                        ) => void,
                    }}
                    addClassNames={{
                        dropzone:
                            'bg-transparent p-0 overflow-hidden border-0',
                        inputLabel:
                            'text-gray-400 font-medium text-lg flex-grow',
                        submitButtonContainer: 'mt-3',
                        submitButton:
                            'flex gap-3 items-center bg-primary-light text-white wrap-upload-btn rounded-lg w-full text-lg font-serif font-light justify-center',
                        labelStyle: '',
                    }}
                />
            )
        }
    }

    return (
        <form method="POST" onSubmit={handleSubmit(onSubmit)}>
            {/* Profile Photo*/}
            <div id="staff-general-form" className="flex items-center profile-upload-wrap">
                {!!api_error?.message?.length && (
                    <GenericError
                        error_message={api_error?.message}
                        onCloseModal={() => {
                            setApiError(DEFAULT_ERROR_STATE)
                        }}
                        tag={api_error?.tag}
                    />
                )}
                <fieldset className="lg:pb-0">
                    {showUploadView()}
                    {!is_tenant ? (
                        <Switch.Group>
                            <div className="flex items-center">
                                <Switch
                                    name="online"
                                    id="online"
                                    checked={login}
                                    onChange={handleLoginChange}
                                    className={`${login
                                        ? 'bg-primary-light'
                                        : 'bg-gray-300'
                                        }
                                        block relative inline-flex flex-shrink-0 h-5 w-10 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 
                                        focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`${login
                                            ? 'translate-x-5'
                                            : 'translate-x-0'
                                            }
                                            pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200`}
                                    />
                                </Switch>
                                <Switch.Label className="ml-4 text-sm">
                                    <Translation content_key="staff.enable_login" />
                                </Switch.Label>
                                <i
                                    className="feather-info mx-2"
                                    ref={login_anchor}
                                    onMouseEnter={() =>
                                        setShowLoginInfo(true)
                                    }
                                    onMouseLeave={() =>
                                        setShowLoginInfo(false)
                                    }
                                />
                            </div>
                        </Switch.Group>
                    ) : (
                        ''
                    )}
                </fieldset>
                
            </div>

            {/* First and Last Name */}
            <div className="pb-5 lg:flex gap-5">
                <fieldset className="lg:pb-0 lg:w-1/2 aot-form-group">
                    <FormInput
                        label='first_name'
                        htmlFor='first-name'
                        fieldError={errors.first_name}
                        required
                    >
                        <input
                            type="text"
                            {...register('first_name', {
                                required: "common.first_name_required",
                                pattern: {
                                    value: WORD_SINGLE_LINE_REGEX,
                                    message: 'invalid.pattern.first_name'
                                }
                            })}
                            defaultValue={(attributes?.first_name as string) || ''}
                        />
                    </FormInput>
                </fieldset>
                <fieldset className="lg:w-1/2 aot-form-group">
                    <FormInput
                        label='last_name'
                        htmlFor='last-name'
                        fieldError={errors.last_name}
                        required
                    >
                        <input
                            type="text"
                            {...register('last_name', {
                                required: "common.last_name_required",
                                pattern: {
                                    value: WORD_SINGLE_LINE_REGEX,
                                    message: 'invalid.pattern.last_name'
                                }
                            })}
                            defaultValue={(attributes?.last_name as string) || ''}
                        />
                    </FormInput>
                </fieldset>
            </div>

            {/* Phone and Email */}
            <div className="pb-5 lg:flex gap-5">
                <fieldset className="lg:pb-0 lg:w-1/2 aot-form-group">
                    <FormInput
                        label='phone'
                        htmlFor='phone'
                        fieldError={errors.phone}
                    >
                        <input
                            type="text"
                            {...register('phone', {
                                pattern: {
                                    value: PHONE_REGEX,
                                    message: 'invalid.pattern.phone'
                                }
                            })}
                            defaultValue={(attributes?.phone as string) || ''}
                        />
                    </FormInput>
                </fieldset>

                <fieldset className="lg:w-1/2 aot-form-group">
                    <FormInput
                        label='email'
                        htmlFor='email-address'
                        fieldError={errors.email}
                        required
                    >
                        <input
                            type="text"
                            {...register('email', {
                                required: 'common.email_required',
                                pattern: {
                                    value: EMAIL_REGEX,
                                    message: 'invalid.pattern.email'
                                },
                            })}
                            defaultValue={(attributes?.email as string) || ''}
                            title={
                                !!attributes?.id
                                    ? getTranslation(
                                        'common.contact_aot_support',
                                        translations
                                    )
                                    : ''
                            }
                        />
                    </FormInput>
                </fieldset>
            </div>

            {/* Staff Role*/}
            <div className=" lg:flex gap-5">
            <div className="pb-5 lg:w-1/2">
                <fieldset className="lg:pb-0 w-full aot-form-group">
                    <label htmlFor="role" className="block">
                        {translations['staff_role_permissions']}
                        <i
                            className="feather-info mx-2"
                            ref={role_anchor}
                            onMouseEnter={() => setShowRoleInfo(true)}
                            onMouseLeave={() => setShowRoleInfo(false)}
                        />
                    </label>
                    <DropDownList
                        className="bg-white  mt-1 block w-full shadow-sm border border-gray-300 rounded-md text-base"
                        iconClassName="feather-chevron-down"
                        data={ROLES}
                        value={staff_role}
                        textField="text"
                        dataItemKey="id"
                        required={true}
                        itemRender={RoleItemRender}
                        valueRender={RoleValueRender}
                        valueMap={(value) => value && value.id}
                        onChange={handleRoleChange}
                        popupSettings={{
                            className: "z-[102]",
                        }}
                    />
                </fieldset>
            </div>

            {/* Date Joined */}
            <div className="pb-5 lg:w-1/2">
                <fieldset className=" aot-form-group staff-date">
                    <label
                        htmlFor="date-joined"
                        className={classNames(
                            'block',
                            errors.date_joined?.type ? 'text-red-700' : ''
                        )}
                    >
                        {translations['date_joined']}
                    </label>
                    <DatePickerInput
                        className="px-6 py-3 mt-1 bg-gray-100/50 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md calendar"
                        selected={date_joined}
                        onChange={(v: Date) => {
                            setDateJoined(v)
                        }}
                        disabled={true}
                        dateFormat="dd MMM, yyyy"
                    />
                    {errors.date_joined?.type === 'pattern' && (
                        <span className="text-sm text-red-700">
                            {translations['valid_date_required']}
                        </span>
                    )}
                </fieldset>
            </div>
            </div>
          

            {/* Notes */}
            <fieldset className="pb-5 aot-form-group">
                <FormInput
                    label='notes'
                    htmlFor='notes'
                    fieldError={errors.notes}
                >
                    <textarea
                        id="notes"
                        className='placeholder-gray-300 mt-1 h-40 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md px-6 py-3'
                        placeholder={getTranslation(
                            'private_notes_staff',
                            translations
                        )}
                        {...register('notes', {
                            required: false,
                            value: (attributes?.notes as string) || '',
                            maxLength: 2000
                        })}
                    />
                </FormInput>
            </fieldset>

            {/* Buttons */}
            <div>
                {Object.values(CognitoErrorTypes).includes(
                    errors.email?.type as CognitoErrorTypes
                ) ? (
                    <div className="bg-red-100 rounded px-3 py-1 mb-5">
                        {errors.email?.message && (
                            <span className="text-sm text-red-700">
                                {errors.email?.message}
                            </span>
                        )}
                    </div>
                ) : (
                    ''
                )}

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        className="border border-gray-300 rounded-lg inline-block btn-gap"
                        onClick={onCloseClick}
                    >
                        {translations['cancel']}
                    </button>
                    <button
                        type="submit"
                        className={classNames(
                            'group relative flex justify-center',
                            'btn-gap border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                        )}
                    >
                        {translations['save']}
                    </button>
                </div>
            </div>

            <Popup
                anchor={login_anchor.current}
                show={show_login_info}
                animate={false}
                popupAlign={{
                    horizontal: 'left',
                    vertical: 'top',
                }}
                popupClass={
                    'border shadow-sm rounded-lg w-96 px-5 py-3 mt-2'
                }
                className="z-[102]"
            >
                <p className="font-semibold">
                    <Translation content_key="staff.login_info" />
                </p>
            </Popup>

            <Popup
                anchor={role_anchor.current}
                show={show_role_info}
                animate={false}
                popupAlign={{
                    horizontal: 'left',
                    vertical: 'top',
                }}
                popupClass={
                    'border shadow-sm rounded-lg w-96 px-5 py-3 mt-2'
                }
                className="z-[102]"
            >
                <p className="font-semibold">
                    {translations['staff_role_select']}
                </p>
                {ROLES.map((dataItem) => {
                    return (
                        <div
                            className="bg-gray-100 rounded-lg my-2 p-3"
                            key={dataItem.id}
                        >
                            <p className="font-semibold text-primary">
                                {translations[dataItem.text]}
                            </p>
                            <p className="">
                                {translations[dataItem.desc]}
                            </p>
                        </div>
                    )
                })}
            </Popup>
        </form>
    )
}

export default GeneralForm
